import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Sparkles } from 'lucide-react';
import { HC_FALLBACK_COURSE_IMG } from './api';

export const coursePath = (c) => (c.next_lesson_id ? `/helpconnect/courses/${c.id}/lessons/${c.next_lesson_id}` : `/helpconnect/courses/${c.id}`);

export const courseCta = (c) => {
  if (c.locked) return 'Locked';
  if (c.status === 'completed') return 'Review Course';
  if (c.status === 'not_started') return 'Start Course';
  return 'Continue Learning';
};

export const ProgressBar = ({ value, testId }) => (
  <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden" data-testid={testId}>
    <div className="h-full rounded-full bg-[#922A8C] transition-[width] duration-700" style={{ width: `${Math.min(100, value || 0)}%` }} />
  </div>
);

export const OrientationBadge = () => (
  <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#4c124a] text-white text-xs font-semibold px-3 py-1" data-testid="hc-orientation-badge">
    <Sparkles className="w-3.5 h-3.5" /> Start here
  </span>
);

export const LockOverlay = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow"><Lock className="w-5 h-5 text-[#4c124a]" /></div>
  </div>
);

export const LockedNote = ({ message, id }) => (
  <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#F6EAF5] px-3 py-2 text-sm font-medium text-[#4c124a]" data-testid={`hc-course-locked-${id}`}>
    <Lock className="w-4 h-4 mt-0.5 shrink-0" /> {message || 'Complete Orientation to unlock this course'}
  </div>
);

export const CourseImage = ({ course, className }) => (
  <div className="relative">
    <img src={course.image_url || HC_FALLBACK_COURSE_IMG} alt="" className={`${className} ${course.locked ? 'grayscale opacity-60' : ''}`} />
    {course.is_orientation && <OrientationBadge />}
    {course.locked && <LockOverlay />}
  </div>
);

export const CourseCard = ({ course, primary }) => {
  const navigate = useNavigate();
  const unavailable = course.status === 'unavailable';
  const blocked = unavailable || course.locked;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow" data-testid={`hc-course-card-${course.id}`}>
      <CourseImage course={course} className="h-40 w-full object-cover" />
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-bold text-lg text-[#363636] leading-snug">{course.title}</h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{course.description}</p>
        <div className="mt-auto pt-4">
          <div className="text-sm font-medium text-[#363636] mb-1.5" data-testid={`hc-course-progress-${course.id}`}>{course.progress}% Complete</div>
          <ProgressBar value={course.progress} />
          <div className="text-sm text-gray-500 mt-2">{course.completed_lessons} of {course.total_lessons} lessons</div>
          {course.locked && <LockedNote message={course.locked_message} id={course.id} />}
          <button disabled={blocked} onClick={() => navigate(coursePath(course))} data-testid={`hc-course-cta-${course.id}`}
            className={`mt-4 w-full h-11 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${primary ? 'bg-[#922A8C] text-white hover:bg-[#7a2275]' : 'border-2 border-[#922A8C] text-[#922A8C] hover:bg-[#F6EAF5]'}`}>
            {unavailable && !course.locked ? 'Temporarily unavailable' : courseCta(course)} {!blocked && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
